import Scaffolds from "@repo/core/components/layout/Scaffolds";
import { StatusBarVariantEnum } from "@repo/core/components/layout/StatusBar/types";
import { Camera } from "@repo/core/components/core/Camera";
import { CaptureDocumentController } from "./controller";
import { MustShow } from "@repo/core/components/core/MustShow";
import { VStack, Image, Text } from "@gluestack-ui/themed";
import { Button } from "@repo/core/components/core/Button";
import { SelfieMask, DocumentMask } from "./components";
import { RequiredDocumentTypeEnum } from "@repo/core/enums/proposals";
import { useCaptureDocumentOrientation } from "@repo/core/hooks/useCaptureDocumentOrientation";

type PermissionStateProps = {
  isCheckingPermissions: boolean;
  permission: { granted: boolean; canAskAgain: boolean } | null;
  handleRequestPermission: () => void;
};

const renderPermissionState = ({
  isCheckingPermissions,
  permission,
  handleRequestPermission
}: PermissionStateProps) => {
  if (isCheckingPermissions) {
    return (
      <VStack alignItems="center" gap="$4">
        <Text
          color="$textLight0"
          fontSize="$lg"
          fontWeight={600}
          textAlign="center"
        >
          Verificando permissões da câmera...
        </Text>
        <Button text="Loading..." isLoading isDisabled />
      </VStack>
    );
  }

  if (!permission) {
    return (
      <VStack alignItems="center" gap="$4">
        <Text
          color="$textLight0"
          fontSize="$lg"
          fontWeight={600}
          textAlign="center"
        >
          Permissão para acessar a câmera necessária
        </Text>
        <Text color="$textLight400" fontSize="$sm" textAlign="center" px="$4">
          Precisamos de acesso à sua câmera para capturar documentos. Por favor,
          clique no botão abaixo para permitir o acesso.
        </Text>
        <Button
          text="Solicitar Permissão para acessar a câmera"
          onPress={handleRequestPermission}
          size="xl"
        />
      </VStack>
    );
  }

  if (!permission.granted && permission.canAskAgain) {
    return (
      <VStack alignItems="center" gap="$4">
        <Text
          color="$textLight0"
          fontSize="$lg"
          fontWeight={600}
          textAlign="center"
        >
          Acesso à câmera necessário
        </Text>
        <Text color="$textLight400" fontSize="$sm" textAlign="center" px="$4">
          Para capturar documentos, precisamos de acesso à sua câmera. Por
          favor, permita o acesso à câmera quando solicitado.
        </Text>
        <Button
          text="Permitir Acesso à Câmera"
          onPress={handleRequestPermission}
          size="xl"
        />
      </VStack>
    );
  }

  return (
    <VStack alignItems="center" gap="$4">
      <Text
        color="$textLight0"
        fontSize="$lg"
        fontWeight={600}
        textAlign="center"
      >
        Permissão para acessar a câmera foi negada.
      </Text>
      <Text color="$textLight400" fontSize="$sm" textAlign="center" px="$4">
        Acesso à câmera foi negado. Para capturar documentos, por favor,
        habilite a permissão de acesso à câmera nas configurações do seu
        dispositivo.
      </Text>
    </VStack>
  );
};

const isManualBiometryDocument = (documentType?: RequiredDocumentTypeEnum) =>
  documentType === RequiredDocumentTypeEnum.BIOMETRY ||
  documentType === RequiredDocumentTypeEnum.BIOMETRY_FACE;

export const CaptureDocument = () => {
  const {
    onCaptureImage,
    cameraFacing,
    capturedImage,
    onSaveImage,
    onChangeImage,
    onCancelCapture,
    onToggleCameraFacing,
    isUploading,
    currentDocumentCapture,
    permission,
    isCheckingPermissions,
    handleRequestPermission
  } = CaptureDocumentController();
  const isDocumentCapture = [
    RequiredDocumentTypeEnum.DOCUMENT_FRONT,
    RequiredDocumentTypeEnum.DOCUMENT_BACK
  ].includes(currentDocumentCapture);
  const isBiometryCapture = isManualBiometryDocument(currentDocumentCapture);

  useCaptureDocumentOrientation(currentDocumentCapture);

  return (
    <Scaffolds.Root
      flex={1}
      height="$100vh"
      position="relative"
      statusBarVariant={StatusBarVariantEnum.CAMERA}
      title="Capturar Foto"
      showHeader={false}
    >
      <MustShow
        isVisible={!!capturedImage}
        fallback={
          <MustShow
            isVisible={!isCheckingPermissions && permission?.granted === true}
            fallback={
              <VStack
                flex={1}
                alignItems="center"
                justifyContent="center"
                bg="$backgroundDark950"
                p="$6"
                gap="$4"
              >
                {renderPermissionState({
                  isCheckingPermissions,
                  permission,
                  handleRequestPermission
                })}
              </VStack>
            }
          >
            <VStack
              flex={1}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
            >
              <Camera
                facing={cameraFacing}
                isScanDisabled={true}
                onImageCaptured={onCaptureImage}
                onCancelCapture={onCancelCapture}
                onToggleCameraFacing={
                  isBiometryCapture ? onToggleCameraFacing : undefined
                }
                responsiveOrientationWhenOrientationLocked={isDocumentCapture}
                shutterPlacement={
                  isDocumentCapture ? "right-center" : "bottom-center"
                }
              />
              {/* Show mask based on document type */}
              {isBiometryCapture && <SelfieMask />}
              {isDocumentCapture && <DocumentMask />}
            </VStack>
          </MustShow>
        }
      >
        <VStack
          flex={1}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
        >
          <Image
            source={{ uri: capturedImage || "" }}
            style={{ flex: 1, width: "100%" }}
            alt="Captured Image"
          />
          <VStack
            p="$4"
            w="$full"
            position="absolute"
            bottom="$4"
            gap="$2"
            alignItems="center"
          >
            <Button
              w="$full"
              text="Salvar"
              onPress={onSaveImage}
              isLoading={isUploading}
              isDisabled={isUploading}
            />
            <Button
              w="$full"
              text="Tirar outra"
              action="secondary"
              onPress={onChangeImage}
              isDisabled={isUploading}
            />
          </VStack>
        </VStack>
      </MustShow>
    </Scaffolds.Root>
  );
};
